import { Button, FormControl, FormLabel, Stack, TextField, Typography } from "@mui/material";
import { Formik, getIn } from "formik";
import { isEmpty } from "lodash-es";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useUserAffiliation } from "@/common/hooks/user/useUserAffiliation";
import { BaseEntityDefaultValues } from "@/common/ts/entity";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  CreateProductLocationDto,
  EntityType,
  LocationDto,
  ProductLocationDto,
  UpdateProductLocationDto,
} from "@/core/api/generated/v1";

import GeneralValidationError from "../../Error/GeneralValidationError";
import FormActions from "../../Form/FormActions";
import AppTextArea from "../../Form/Input/AppTextArea";
import EntityAffiliationInputV2 from "../EntityAffiliation/EntityAffiliationInputV2";
import GeneralAddressInput from "../General/GeneralAddress/GeneralAddressInput";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
} from "../components/BaseEntityCreateUpdate";

type DefaultValues = BaseEntityDefaultValues & {
  sourceLocation?: LocationDto | null;
  locationIds?: CreateProductLocationDto["locationIds"];
  departmentIds?: CreateProductLocationDto["departmentIds"];
  name?: CreateProductLocationDto["name"];
};

export interface ProductLocationCreateUpdateOwnProps extends BaseEntityCreateUpdateInheritableProps<
  ProductLocationDto,
  DefaultValues
> {
  productLocationId?: string;
}

export type ProductLocationCreateUpdateProps = ProductLocationCreateUpdateOwnProps;

export default function ProductLocationCreateUpdate({
  productLocationId,
  defaultValues,
  onCreate,
  onUpdate,
  onSave,
}: ProductLocationCreateUpdateProps) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();
  const userAffiliation = useUserAffiliation();

  const isCreate = !productLocationId;

  const productLocationRequest = useApiRequest(
    apiClient.productLocationsApi.apiV1ProductLocationsProductLocationIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      productLocationId: productLocationId!,
    },
    {
      skip: !productLocationId,
    },
  );
  const productLocation = productLocationRequest?.data;

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb: productLocation && {
      idValue: productLocation.id!,
      newTitle: productLocation.localNumber || "",
    },
  });

  return (
    <BaseEntityCreateUpdate
      entityType={EntityType.ProductLocation}
      entityId={productLocationId}
      entity={productLocation}
      entityRequest={productLocationRequest}
      isIniting={userAffiliation.isIniting}
    >
      <Formik<CreateProductLocationDto & UpdateProductLocationDto & BaseFormikValues>
        enableReinitialize={true}
        initialValues={{
          departmentIds:
            (!isEmpty(defaultValues?.departmentIds) ? defaultValues!.departmentIds : undefined) ||
            (defaultValues?.sourceLocation?.department
              ? [defaultValues.sourceLocation.department.id!]
              : undefined) ||
            (!isEmpty(productLocation?.departmentIds)
              ? productLocation!.departmentIds
              : undefined) ||
            (!isEmpty(userAffiliation.departments)
              ? userAffiliation.departments!.map((d) => d.id!)
              : undefined),
          locationIds:
            (!isEmpty(defaultValues?.locationIds) ? defaultValues!.locationIds : undefined) ||
            (defaultValues?.sourceLocation ? [defaultValues.sourceLocation.id!] : undefined) ||
            (!isEmpty(productLocation?.locationIds) ? productLocation!.locationIds : undefined) ||
            (!isEmpty(userAffiliation.locations)
              ? userAffiliation.locations!.map((l) => l.id!)
              : undefined),
          name:
            defaultValues?.name ||
            defaultValues?.sourceLocation?.name ||
            productLocation?.name ||
            undefined,
          description:
            defaultValues?.sourceLocation?.description || productLocation?.description || undefined,
          address: defaultValues?.sourceLocation?.address || productLocation?.address || undefined,
          submit: "",
        }}
        validationSchema={Yup.object().shape({
          // vehicleId: Yup.string().required("Vehicle is required"),
        })}
        onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
          try {
            if (isCreate) {
              const response = await apiClient.productLocationsApi.apiV1ProductLocationsPost({
                nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                createProductLocationDto: {
                  ...values,
                },
              });
              enqueueSnackbar("Sales location created.", { variant: "success" });
              onCreate && onCreate(response.data);
              onSave && onSave(response.data);
            } else {
              const response =
                await apiClient.productLocationsApi.apiV1ProductLocationsProductLocationIdPut({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  productLocationId,
                  updateProductLocationDto: {
                    ...values,
                  },
                });
              enqueueSnackbar("Sales location updated.", { variant: "success" });
              onUpdate && onUpdate(response.data);
              onSave && onSave(response.data);
            }

            if (mounted.current) {
              setStatus({ success: true });
              setSubmitting(false);
            }
          } catch (err: any) {
            if (mounted.current) {
              ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
              setStatus({ success: false });
              setSubmitting(false);
            }
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setErrors,
          setFieldValue,
          setValues,
        }) => {
          return (
            <form noValidate onSubmit={handleSubmit}>
              <FormContentBlock>
                {/* Entity affiliation */}
                <EntityAffiliationInputV2
                  departments={{
                    departmentIds: values.departmentIds,
                    onChange: (departmentIds) => {
                      setFieldValue("departmentIds", departmentIds);
                    },
                    error: errors.departmentIds,
                  }}
                  locations={{
                    locationIds: values.locationIds,
                    onChange: (locationIds) => {
                      setFieldValue("locationIds", locationIds);
                    },
                    error: errors.locationIds,
                  }}
                />

                <TextField
                  error={Boolean(touched.name && errors.name)}
                  required
                  fullWidth
                  helperText={touched.name && errors.name}
                  label='Name'
                  margin='dense'
                  name='name'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type='text'
                  value={values.name || ""}
                  variant='outlined'
                />

                <AppTextArea
                  error={Boolean(touched.description && errors.description)}
                  fullWidth
                  helperText={touched.description && errors.description}
                  mode='description'
                  margin='dense'
                  name='description'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.description || ""}
                  variant='outlined'
                />

                <FormControl fullWidth>
                  <Stack spacing={1}>
                    <FormLabel required>
                      <Typography component='span' variant='subtitle1'>
                        Address
                      </Typography>
                    </FormLabel>

                    <GeneralAddressInput
                      errors={getIn(errors, "address")}
                      touched={getIn(touched, "address")}
                      value={values.address}
                      inputsProps={{ all: {} }}
                      onChange={(newValue) => setFieldValue("address", newValue)}
                    />
                  </Stack>
                </FormControl>
              </FormContentBlock>

              <GeneralValidationError sx={{ my: 1 }} errors={errors} />

              <FormActions>
                <Button
                  color='primary'
                  loading={isSubmitting}
                  fullWidth
                  type='submit'
                  variant='contained'
                >
                  Save
                </Button>
              </FormActions>
            </form>
          );
        }}
      </Formik>
    </BaseEntityCreateUpdate>
  );
}
